import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Factory, Package, Settings, Plus, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import type { AppStep } from '@/types/app';
import apiClient from '@/lib/apiClient';

interface DashboardStats {
  totalProducts: number;
  thisMonth: number;
  processingJobs: number;
}

interface DashboardPageProps {
  onNavigate: (step: AppStep) => void;
}

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/stats');
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError(response.data.error || 'Failed to load stats');
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Factory className="h-4 w-4" />
          <span>AI-Powered</span>
        </div>
        <h1 className="text-4xl font-bold mb-3">Welcome to EtsyFlow Studio</h1>
        <p className="text-muted-foreground text-lg">
          Streamline your digital art production workflow with AI-powered tools and intelligent automation.
        </p>
      </div>

      {/* Stats Cards */}
      {error ? (
        <Card className="border-destructive">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle className="text-destructive">Failed to load stats</CardTitle>
            </div>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Products</CardDescription>
              <CardTitle className="text-3xl">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.totalProducts ?? 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Products in your library</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>This Month</CardDescription>
              <CardTitle className="text-3xl">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.thisMonth ?? 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Products created this month</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Processing</CardDescription>
              <CardTitle className="text-3xl">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.processingJobs ?? 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Mockup jobs in progress</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Get started with your product creation workflow</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            className="w-full justify-start h-auto py-4"
            onClick={() => onNavigate('upload')}
          >
            <Plus className="h-5 w-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">Create New Product</div>
              <div className="text-xs font-normal opacity-80">Upload master image and start production</div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start h-auto py-4"
            onClick={() => onNavigate('products')}
          >
            <Package className="h-5 w-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">View All Products</div>
              <div className="text-xs font-normal text-muted-foreground">Browse and manage your product library</div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start h-auto py-4"
            onClick={() => onNavigate('settings')}
          >
            <Settings className="h-5 w-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">Settings</div>
              <div className="text-xs font-normal text-muted-foreground">Configure PDF templates and README files</div>
            </div>
          </Button>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <Card>
        <CardHeader>
          <CardTitle>Getting Started</CardTitle>
          <CardDescription>Follow these steps to set up your product creation workflow</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              1
            </div>
            <div>
              <h4 className="font-semibold mb-1">Configure your API settings</h4>
              <p className="text-sm text-muted-foreground">
                Set up your API credentials in the Settings page to enable AI-powered content generation.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              2
            </div>
            <div>
              <h4 className="font-semibold mb-1">Create your first product</h4>
              <p className="text-sm text-muted-foreground">
                Use the Product Creation workflow to generate AI-Generated content with AI assistance.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              3
            </div>
            <div>
              <h4 className="font-semibold mb-1">Review and manage products</h4>
              <p className="text-sm text-muted-foreground">
                Monitor your products, track performance, and manage your content library.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

